import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import ResponsiveIframe from "components/ResponsiveIframe";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useIntl } from "react-intl";
import { Link, Navigate, useParams, useLocation } from "react-router-dom";
import { ResponseOfAgreementResponse } from "services/KnowfullyAdminClient";

const styles = {
  paper: {
    border: "4px solid #F0E4D3",
    borderRadius: "5px",
    margin: "20px",
  },
};

const AgreementDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const {
    agreementService,
    agreementsUrl,
    agreementEditUrl,
  } = useGlobalContext();

  const agreementResponse = useStrictQuery<ResponseOfAgreementResponse>(
    ["agreementService.getAgreement", id],
    () => agreementService.getAgreement(id!),
    {
      enabled: !!id,
    }
  );

  const agreement = agreementResponse.data?.data;
  useTitle(agreement?.name || "Agreement Details");

  if (!id) return <Navigate to={agreementsUrl()} replace />;
  if (agreementResponse.isLoading) return <Loader />;

  const rows = [
    {
      title: t({ id: "id" }),
      component: agreement?.id && <span>{agreement.id}</span>,
    },
    {
      title: t({ id: "organization" }),
      component: agreement?.organizationName && (
        <span>{agreement.organizationName}</span>
      ),
    },
    {
      title: t({ id: "nameLabel" }),
      component: agreement?.name && <span>{agreement.name}</span>,
    },
    {
      title: t({ id: "agreementDisplayNameLabel" }),
      component: agreement?.displayName && <span>{agreement.displayName}</span>,
    },
    {
      title: t({ id: "statusLabel" }),
      component: agreement?.agreementStatusName && (
        <span>{agreement.agreementStatusName}</span>
      ),
    },
    {
      title: t({ id: "agreementContentTitle" }),
      component: agreement?.content && (
        <Paper sx={styles.paper}>
          <ResponsiveIframe srcDoc={agreement.content} title="contentHtml" />
        </Paper>
      ),
    },
  ].filter((r) => !!r.component);

  const backButtonProps = location.state?.backButtonProps;

  return (
    <>
      <RedirectBackButton
        redirectRoute={agreementsUrl()}
        labelKey="backToListButtonLabel"
        {...backButtonProps}
      />
      <h2>{agreement?.name}</h2>

      <Box my={3} display="flex" justifyContent="center">
        <Link to={agreementEditUrl(id)} style={{ textDecoration: "none" }}>
          <Fab color="primary">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default AgreementDetails;
